// DialogComponent.js
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Row, Col, Modal, Input, Button, Space, message, Badge, Alert } from 'antd';
import { SendOutlined, EditOutlined, RobotOutlined } from '@ant-design/icons';

import styles from './index.less';
import useKnowledgeProduct from '@/pages/knowledge/hooks/useKnowledgeProduct';
import useSocket from './hooks/useSocket';

import { request } from '@/utils/request';
import Comments from './components/comments';
import SessionItem from './components/session-item';

import useResultModal from '@/pages/knowledge/hooks/useResultModal';

export type Session = {
  id: string;
  name: string;
  product: string;
  unreadCount: number;
  createdAt: string;
}

export type ChatHistory = {
  id: string;
  sessionId: string;
  from: string;
  to: string;
  content: string;
  read: boolean;
  predictionId?: number;
  createdAt: string;
}

const Index: React.FC = () => {
  const [visible, setVisible] = useState<boolean>(false);
  const [sessions, setSession] = useState<Session[]>([]);
  const [activeChat, setActiveChat] = useState<string>("");
  const [create, setCreate] = useState<boolean>(false);
  const [activeProduct, setActiveProduct] = useState<string>("");
  const [unreadCountList, setUnreadCountList] = useState<{ id: string; unread: number }[]>([]);

  const [sessionMap, setSessionMap] = useState<{ [sessionId: string]: ChatHistory[] }>({});

  const [text, setText] = useState<string>("");

  const [sendLoading, setSendLoading] = useState<boolean>(false);

  const { } = useSocket(callbackMessage, callbackCount);

  const { E: ResultModal, setController } = useResultModal()

  const inputWrapperRef = useRef<any>();
  const rightWrapperRef = useRef<any>();

  const { data: productNames } = useKnowledgeProduct();

  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchSessions();
  }, []);

  useEffect(() => {
    if (activeChat) {
      fetchChatHistory();
    }
  }, [activeChat]);

  useEffect(() => {
    if (visible && activeChat) {
      handleRead();
    }
  }, [visible, activeChat, sessionMap])

  const handleScroll = (value: number, behavior: 'instant' | 'smooth' | 'auto') => {
    if (wrapperRef.current) wrapperRef.current.scrollTo({ top: value, behavior });
  }

  const handleRead = () => {
    request('/local/simulation/chatHistory/read', {
      method: 'POST',
      data: {
        sessionId: activeChat,
      }
    })
  }

  const activeMessages: ChatHistory[] = useMemo(() => {
    return sessionMap[activeChat] || [];
  }, [activeChat, sessionMap]);

  const unreadCount = useMemo(() => {
    let count = 0;
    unreadCountList.forEach(vo => {
      count += vo.unread;
    })
    return count;
  }, [unreadCountList]);

  function callbackCount(data: any) {
    setUnreadCountList(data);
  }


  function callbackMessage(data: ChatHistory) {
    if (sessionMap[data.sessionId] && sessionMap[data.sessionId].some(vo => vo.id === data.id)) {
      return;
    }
    setSessionMap(pre => {
      if (pre[data.sessionId]) {
        return {
          ...pre,
          [data.sessionId]: [...pre[data.sessionId], data]
        }
      }
      return {
        ...pre,
        [data.sessionId]: [data]
      }
    })
  }

  const sendMessage = async () => {
    if (sendLoading) return;
    if (!text) return message.warning("请输入内容！");
    if (create) {
      if (!activeProduct) return message.warning("请先选择产品！");
      setSendLoading(true);
      const sessionId = await createSession()
      await sendChat(sessionId);
      setCreate(false);
    } else {
      if (!activeChat) return message.warning("请新建或选择对话！");
      setSendLoading(true);
      await sendChat();
    }
    setSendLoading(false);
    setText("");
  }

  async function fetchSessions() {
    const { resp } = await request('/local/simulation/session', {});
    if (resp?.code !== 0) return;
    setSession([...sessions, ...resp.data.list]);
    if (resp.data.list.length > 0) {
      setActiveChat(resp.data.list[0].id);
    }
  }

  async function createSession() {
    const { resp, error } = await request('/local/simulation/session', {
      method: 'POST',
      data: {
        name: productNames.find(p => p.productName === activeProduct)?.productZhName || activeChat,
        product: activeProduct,
      }
    });
    if (resp?.code !== 0) return message.error(error?.message);
    // message.success(resp?.message);
    const sessionId = resp.data.value.id;
    setSessionMap(pre => ({ ...pre, [sessionId]: [] }));
    setSession([resp.data.value, ...sessions]);
    setActiveChat(sessionId);
    return sessionId;
  }

  async function fetchChatHistory() {
    if (sessionMap[activeChat]) return;
    const { resp } = await request("/local/simulation/chatHistory", {
      params: {
        sessionId: activeChat,
      }
    });
    if (resp?.code !== 0) return;
    setSessionMap(pre => {
      if (pre[activeChat]) {
        return {
          ...pre,
          [activeChat]: [...resp.data.list, ...pre[activeChat]]
        }
      }
      return {
        ...pre,
        [activeChat]: resp.data.list
      }
    });
  }

  async function sendChat(sessionId?: string) {
    const { resp, error } = await request('/local/simulation/chatHistory', {
      method: "POST",
      data: {
        sessionId: sessionId ?? activeChat,
        content: text,
      }
    });
    if (resp?.code !== 0) return message.error(error?.message);

    callbackMessage(resp.data.value);
  }

  function getSessionUnreadCount(sessionId: string) {
    const session = unreadCountList.find(u => u.id === sessionId);
    if (!session) return 0;
    return session.unread;
  }

  const handleEvent = async (e: string, sessionId: string, text?: string) => {
    switch (e) {
      case '清空': {
        const { resp } = await request(`/local/simulation/session/clear`, {
          method: 'POST',
          data: { id: sessionId }
        });
        if (resp?.code !== 0) return message.error(resp?.message);
        message.success(resp.message);
        setSessionMap(pre => ({ ...pre, [sessionId]: [] }));
        if (sessionId === activeChat) setActiveChat("");
        break;
      }

      case '删除': {
        const { resp } = await request(`/local/simulation/session/${sessionId}`, {
          method: 'DELETE',
        });
        if (resp?.code !== 0) return message.error(resp?.message);
        message.success(resp.message);
        setSession(pre => pre.filter(s => s.id !== sessionId));
        setSessionMap(pre => ({ ...pre, [sessionId]: [] }));
        break;
      }
      case '编辑名称':
        const { resp } = await request(`/local/simulation/session/${sessionId}`, {
          method: 'PUT',
          data: { name: text }
        });
        if (resp?.code !== 0) return message.error(resp?.message);

        setSession(sessions.map(vo => {
          if (vo.id === sessionId) {
            return { ...vo, name: text! }
          }
          return vo
        }))
        break;
    }
  }

  const handlePredictInfo = async (predictionId: number) => {
    const { resp } = await request(`/local/chat/completion/${predictionId}`);
    if (resp?.code !== 0) return message.error(resp?.message);
    setController({ visible: true, text: <>提示词</>, footer: <Alert description={<div style={contentStyle}>{JSON.stringify(JSON.parse(resp.data.chat_completion_prompt), null, 4)}</div>} /> })
  }

  const contentStyle = { fontSize: "12px", maxHeight: 500, overflowY: 'auto', whiteSpace: 'pre-wrap' } as React.CSSProperties;

  return <div>
    <Badge count={unreadCount} overflowCount={100}>
      <a onClick={() => setVisible(true)}><RobotOutlined style={{ fontSize: 20 }} /></a>
    </Badge>
    <Modal
      visible={visible}
      footer={false}
      width={'600px'}
      bodyStyle={{ padding: 0, background: 'transparent !important' }}
      wrapClassName={styles.modal}
      onCancel={() => setVisible(false)}
    >
      <Row className={styles.wrapper}>
        <Col span={6} className={styles.chatWrapper}>
          <div className={styles.chatItem} style={{ width: '100%', display: 'flex', flexWrap: "nowrap", justifyContent: 'space-between', alignItems: 'center' }} onClick={() => setCreate(true)}>新对话<EditOutlined /></div>
          {
            sessions.map(vo => <SessionItem
              key={vo.id}
              vo={vo}
              activeChat={activeChat}
              count={getSessionUnreadCount(vo.id)}
              handleEvent={handleEvent}
              handleClick={(id: string) => {
                setCreate(false);
                setActiveChat(id);
                fetchChatHistory();
              }}
            />
            )
          }
        </Col>
        <Col span={18} ref={rightWrapperRef} className={styles.rightWrapper}>
          <div className={styles.messageWrapper} ref={wrapperRef}>
            {
              create ?
                <div>
                  <div style={{ height: 60 }}></div>
                  <h3>请选择产品，再开始对话！</h3>
                  <Space wrap>
                    {
                      productNames.filter(vo => vo.existPrompt).map(vo => <Button
                        key={vo.productName}
                        type={activeProduct === vo.productName ? 'primary' : 'default'}
                        onClick={() => setActiveProduct(vo.productName)}
                      >{vo.productZhName}</Button>)
                    }
                  </Space>
                </div> : <Comments data={activeMessages} handleScroll={handleScroll} handlePredictInfo={handlePredictInfo} />
            }
          </div>
          <div className={styles.inputWrapper} ref={inputWrapperRef}>
            {/* <ScrollableContainer /> */}
            <Input.TextArea
              autoSize={{ minRows: 1, maxRows: 5 }}
              onChange={e => setText(e.target.value)}
              value={text}
              placeholder="请输入内容！"
            />
            <div className={styles.sendWrapper}>
              <Button
                loading={sendLoading}
                onClick={sendMessage}
                className={styles.button}
              >
                <SendOutlined />
              </Button>
            </div>

          </div>
        </Col>
      </Row>
    </Modal>
    {ResultModal}
  </div>;
};

export default Index;
